/**
 * R-166 — Pipeline progress helper.
 *
 * Pre-fix, the BatchDetail pipeline strip was the only visual cue for "how
 * far along is this batch" and it relied entirely on per-stage bucket
 * counts. A doc parked at the export stage (status=in_progress) showed as
 * 0% complete because nothing was rendering its position within the
 * sequence — the operator saw a row of "0"s in the early-stage cells and
 * read it as "no progress."
 *
 * This helper gives partial credit:
 *
 *   stagesPassed = sum over docs of: index of currentStep in PIPELINE_STEP_ORDER
 *     (a doc with status=completed/exported is credited the full chain)
 *   totalStages  = PIPELINE_STEP_ORDER.length
 *   percentComplete = round((stagesPassed / (totalStages * docCount)) * 100)
 *
 * The order mirrors apps/api/src/services/batch-current-step.ts —
 * intentionally duplicated rather than imported so the web bundle doesn't
 * pull in server code. If the canonical sequence changes, update both
 * lists.
 */

export const PIPELINE_STEP_ORDER = [
  "scan",
  "classify",
  // SP3 — remittance steps sit in the keying stage; a remittance pipeline
  // uses one shape (combined XOR split), so at most two of these render.
  "remittance",
  "remittance-key",
  "remittance-balance",
  "key",
  "verify",
  "qc",
  "supervisor",
  "escalation",
  "export",
] as const;

export type PipelineStep = (typeof PIPELINE_STEP_ORDER)[number];

// Statuses that mean "this doc has fully traversed the pipeline" — credit
// the full chain regardless of which step currentStep is parked at. Mirrors
// the COMPLETE_STATUSES set used by the aggregate-status helper.
const COMPLETED_STATUSES = new Set(["completed", "exported", "complete"]);

export interface DocForProgress {
  currentStep: string;
  status: string;
}

export interface BatchProgress {
  /** Rounded percent in [0, 100]. */
  percentComplete: number;
  /** Sum of stages passed across all docs. */
  stagesPassed: number;
  /** Pipeline length (denominator per doc). */
  totalStages: number;
}

function stagesPassedForDoc(doc: DocForProgress, totalStages: number): number {
  // A doc that has fully completed gets the full chain credited, even if
  // currentStep lags behind on a brief state-machine tick.
  if (COMPLETED_STATUSES.has(doc.status)) return totalStages;

  const idx = (PIPELINE_STEP_ORDER as readonly string[]).indexOf(doc.currentStep);
  // Unknown step → zero credit. Defensive: keeps callers from rendering
  // a misleading "fully done" bar if the server adds a new step name.
  if (idx === -1) return 0;
  return idx;
}

export function computeBatchProgress(docs: DocForProgress[]): BatchProgress {
  const totalStages = PIPELINE_STEP_ORDER.length;

  if (docs.length === 0) {
    return { percentComplete: 0, stagesPassed: 0, totalStages };
  }

  let stagesPassed = 0;
  for (const doc of docs) {
    stagesPassed += stagesPassedForDoc(doc, totalStages);
  }

  const percentComplete = Math.round(
    (stagesPassed / (totalStages * docs.length)) * 100,
  );

  return { percentComplete, stagesPassed, totalStages };
}
